import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  X,
  Loader2,
  FileText,
  User,
  Calendar,
  ShieldCheck,
  AlertCircle,
  PenLine,
} from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import axiosInstance from "../../../../utils/axiosInstance";
import { pickBestRange, formatPatientAge } from "../../../../utils/referenceRange";

import TinyMCEFullEditor from "../../../../components/editors/TinyMCEFullEditor";

const API = import.meta.env.VITE_API_BASE_URL;

// Plain-text ↔ HTML helpers used by RICH_TEXT ("Other Text") fields — doctor types
// plain text; storage stays HTML so existing renderers (preview, PDF) keep working.
const escHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const plainTextToHtml = (text) => {
  const t = String(text ?? "");
  if (!t) return "";
  return t
    .split("\n")
    .map((line) => `<p>${escHtml(line) || "&nbsp;"}</p>`)
    .join("");
};

const htmlToPlainText = (html) =>
  String(html ?? "")
    .replace(/<br\s*\/?>(\n)?/gi, "\n")
    .replace(/<\/(p|div|tr|li)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+|\n+$/g, "");

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "align",
  "link",
  "image",
];

const PathologyResultModal = ({
  isOpen,
  onClose,
  patient,
  test,
  order,
  onSave,
}) => {
  const [results, setResults] = useState([]);
  const [existingResult, setExistingResult] = useState(null);
  const [approved, setApproved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");
  // FREE_TEXT report items: { id, title, html }
  const [freeTextItems, setFreeTextItems] = useState([]);

  const [sigLoading, setSigLoading] = useState(false);
  const [sigOptions, setSigOptions] = useState([]);
  const [sigDefaults, setSigDefaults] = useState({
    left: null,
    center: null,
    right: null,
  });

  const [manualSigs, setManualSigs] = useState({
    leftSignatureId: null,
    centerSignatureId: null,
    rightSignatureId: null,
  });

  const { user } = useAuth();
  const quillRef = useRef(null);

  const existingId = existingResult?.id || existingResult?._id || null;
  const patientId = patient?.id || patient?._id || null;
  const testId = test?.id || test?._id || null;
  const orderId = order?.id || order?._id || null;

  const isLocked = false;

  const handleImageInsert = useCallback(() => {
    try {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");
      input.click();

      input.onchange = async () => {
        try {
          const file = input.files?.[0];
          if (!file) return;

          const reader = new FileReader();
          reader.onload = () => {
            try {
              const quill = quillRef.current?.getEditor();
              if (!quill) return;

              const range = quill.getSelection(true);
              quill.insertEmbed(range?.index ?? 0, "image", reader.result);
              quill.setSelection((range?.index ?? 0) + 1);
            } catch (err) {
              console.error("Quill insert embed error:", err);
            }
          };
          reader.readAsDataURL(file);
        } catch (err) {
          console.error("Image read error:", err);
        }
      };
    } catch (err) {
      console.error("Image insert failed:", err);
    }
  }, []);

  const modules = useMemo(() => {
    return {
      toolbar: isLocked
        ? false
        : {
            container: [
              [{ header: [1, 2, 3, false] }],
              ["bold", "italic", "underline"],
              [{ list: "ordered" }, { list: "bullet" }],
              [{ align: [] }],
              ["link", "image"],
              ["clean"],
            ],
            handlers: {
              image: handleImageInsert,
            },
          },
      clipboard: { matchVisual: false },
    };
  }, [isLocked, handleImageInsert]);

  useEffect(() => {
    if (!isOpen) return;

    loadData();

    return () => {
      try {
        setResults([]);
        setExistingResult(null);
        setApproved(false);
        setNotes("");
        setFreeTextItems([]);
        setSigOptions([]);
        setSigDefaults({ left: null, center: null, right: null });
        setManualSigs({
          leftSignatureId: null,
          centerSignatureId: null,
          rightSignatureId: null,
        });
        setLoading(false);
        setSaving(false);
        setSigLoading(false);
      } catch (err) {
        console.error("Cleanup error:", err);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadSignatures = async (existing) => {
    try {
      setSigLoading(true);

      const res = await axiosInstance.get(`${API}/api/esignatures/by-test`, {
        params: { testId },
      });

      const data = res.data?.data || {};
      const defaults = data.defaults || { left: null, center: null, right: null };
      const options = data.signatures || [];

      setSigDefaults(defaults);
      setSigOptions(options);

      setManualSigs({
        leftSignatureId: existing?.leftSignatureId ?? defaults.left ?? null,
        centerSignatureId: existing?.centerSignatureId ?? defaults.center ?? null,
        rightSignatureId: existing?.rightSignatureId ?? defaults.right ?? null,
      });
    } catch (err) {
      console.error("signature load error:", err);
      setSigDefaults({ left: null, center: null, right: null });
      setSigOptions([]);

      setManualSigs({
        leftSignatureId: existing?.leftSignatureId ?? null,
        centerSignatureId: existing?.centerSignatureId ?? null,
        rightSignatureId: existing?.rightSignatureId ?? null,
      });
    } finally {
      setSigLoading(false);
    }
  };

  const loadData = async () => {
    try {
      if (!patientId || !testId || !orderId) return;

      setLoading(true);

      const [paramsRes, resultRes, itemsRes] = await Promise.all([
       axiosInstance.get(`${API}/api/parameters/list/${testId}`, {
  params: { patientId }, // ✅ only this
}),
        axiosInstance
          .get(`${API}/api/results/find`, {
            params: { orderId, testId, patientId },
          })
          .catch(() => ({ data: { data: null } })),
        axiosInstance
          .get(`${API}/api/report-items/list/${testId}`)
          .catch(() => ({ data: { data: [] } })),
      ]);

      const params = paramsRes.data?.data || [];
      const existing = resultRes.data?.data || null;
      const allItems = itemsRes.data?.data || [];

      setExistingResult(existing);
      setApproved(existing?.status === "APPROVED");

      // Parse notes: extract __freeTexts sub-key if present
      let plainNotes = "";
      let savedFreeTexts = {};
      try {
        const notesRaw = existing?.notes || "";
        const parsed = JSON.parse(notesRaw);
        plainNotes = parsed.__notes || "";
        savedFreeTexts = parsed.__freeTexts || {};
      } catch {
        plainNotes = existing?.notes || "";
      }
      setNotes(plainNotes);

      // Use already-fetched report items for FREE_TEXT + RICH_TEXT editors
      // (RICH_TEXT = "Other Text": title set at design time, value entered at result time)
      const ftItems = allItems
        .filter((it) => it.type === "FREE_TEXT" || it.type === "RICH_TEXT")
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((it) => ({
          id: String(it.id),
          type: it.type,
          title: it.title || (it.type === "RICH_TEXT" ? "Other Text" : "Free Text"),
          // Priority: doctor's saved content → design-time default HTML → blank
          html: savedFreeTexts[String(it.id)] ?? it.html ?? "",
        }));
      setFreeTextItems(ftItems);

      await loadSignatures(existing);

      // Build ordered parameter list from report items (matches PDF order)
      const paramReportItems = allItems
        .filter((item) => item.type === "PARAMETER" && (item.parameterId || item.parameter))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

      // Map params by ID for range/detail lookup
      const paramsById = new Map();
      params.forEach((p) => {
        const pid = String(p?.id || p?._id);
        paramsById.set(pid, p);
      });

      // Use report items order; fall back to params list if no report items
      let orderedParams;
      if (paramReportItems.length > 0) {
        const usedIds = new Set();
        orderedParams = paramReportItems
          .map((item) => {
            const pid = String(item.parameterId || item.parameter?.id || item.parameter?._id);
            usedIds.add(pid);
            // Prefer full param from params API (has ranges), fall back to embedded
            return paramsById.get(pid) || item.parameter;
          })
          .filter(Boolean);
        // Append any params not covered by report items
        params.forEach((p) => {
          const pid = String(p?.id || p?._id);
          if (!usedIds.has(pid)) orderedParams.push(p);
        });
      } else {
        orderedParams = params;
      }

      const merged = orderedParams.map((p) => {
        const pid = p?.id || p?._id;
        const saved = existing?.parameterResults?.find(
          (r) => (r.parameterId || r.parameter?._id || r.parameter?.id) === pid
        );

        // Pick the range matching this patient's gender + age (falls back to the first range)
        const range0 = pickBestRange(p?.ranges, patient) || p?.ranges?.[0];

        let displayRange = "";
        if (
          range0?.lowerLimit !== undefined ||
          range0?.upperLimit !== undefined
        ) {
          const lower = range0?.lowerLimit ?? "";
          const upper = range0?.upperLimit ?? "";
          const combined = `${lower} - ${upper}`.trim();
          if (combined !== " - " && combined !== "-") {
            displayRange = combined;
          }
        }

        const normalValueHtml = range0?.normalValueHtml || "";
        const specialConditionHtml = range0?.specialConditionHtml || "";

        return {
          parameterId: pid,
          name: p?.name,
          type: p?.type,
          value:
            saved?.valueText ??
            (saved?.valueNumber !== undefined && saved?.valueNumber !== null
              ? String(saved.valueNumber)
              : ""),
          unit: saved?.unit || p?.unit || "",
          referenceRange: displayRange,
          normalValueHtml,
          specialConditionHtml,
          originalParameter: p,
        };
      });

      setResults(merged);
    } catch (e) {
      console.error("loadData error:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!patientId || !testId || !orderId || !user?.id) return;

      setSaving(true);

      // Serialize notes + freeTexts into a single JSON string for the notes field
      const freeTextsMap = {};
      freeTextItems.forEach((ft) => {
        if (ft.html && ft.html.replace(/<[^>]*>/g, "").trim()) {
          freeTextsMap[ft.id] = ft.html;
        }
      });
      const hasFreetexts = Object.keys(freeTextsMap).length > 0;
      const notesPayload = (notes?.trim() || hasFreetexts)
        ? JSON.stringify({ __notes: notes?.trim() || "", __freeTexts: freeTextsMap })
        : null;

      const payload = {
        testType: "PATHOLOGY",
        testId,
        patientId,
        orderId,
        reportedById: user.id,
        createdById: user.id,
        approve: approved,
        notes: notesPayload,

        leftSignatureId: manualSigs.leftSignatureId ?? null,
        centerSignatureId: manualSigs.centerSignatureId ?? null,
        rightSignatureId: manualSigs.rightSignatureId ?? null,

        parameters: results
          .filter((r) => {
            const t = String(r.type || "").toLowerCase().replace(/\s+/g, "");
            // Free text: keep if it has any non-trivial HTML
            if (t === "freetext" || t === "free_text") {
              return String(r.value || "").replace(/<[^>]*>/g, "").trim() !== "";
            }
            return String(r.value ?? "").trim() !== "";
          })
          .map((r) => {
            const t = String(r.type || "").toLowerCase().replace(/\s+/g, "");
            const raw = String(r.value ?? "").trim();

            if (t === "numeric" && raw !== "" && !isNaN(Number(raw))) {
              return {
                parameterId: r.parameterId,
                // Preserve the exact text the user typed (e.g. "6.0") so
                // trailing zeros survive a save/reload round-trip. valueNumber
                // is still sent for flag/range evaluation on the backend.
                valueText: raw,
                valueNumber: Number(raw),
                unit: r.unit,
              };
            }

            // Free text stores full HTML in valueText
            return {
              parameterId: r.parameterId,
              valueText: r.value || null,
              valueNumber: null,
              unit: r.unit,
            };
          }),
      };

      let res;
      if (existingId) {
        res = await axiosInstance.put(`${API}/api/results/${existingId}`, payload);
      } else {
        res = await axiosInstance.post(`${API}/api/results`, payload);
      }

      onSave?.(res.data?.data);
      onClose?.();
    } catch (e) {
      console.error("save error:", e);
      alert("Failed to save result");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    try {
      if (!dateString) return "Not available";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (err) {
      console.error("formatDate error:", err);
      return "Not available";
    }
  };

  /* ---------------- Utilities ---------------- */
  const isValueInRange = (value, referenceRange) => {
    try {
      if (value === null || value === undefined || value === "") return null;
      if (!referenceRange) return null;

      const cleanValue = String(value).trim();
      const cleanRange = String(referenceRange).trim();

      if (cleanRange.includes(" - ")) {
        const [lowerStr, upperStr] = cleanRange.split(" - ");
        const lower = parseFloat(lowerStr);
        const upper = parseFloat(upperStr);
        const numValue = parseFloat(cleanValue);

        if (isNaN(numValue) || isNaN(lower) || isNaN(upper)) return null;
        return numValue >= lower && numValue <= upper;
      }

      return null;
    } catch (err) {
      console.error("isValueInRange error:", err);
      return null;
    }
  };

  const getResultColor = (value, referenceRange) => {
    const inRange = isValueInRange(value, referenceRange);
    if (value === "") return "border-gray-200";
    if (inRange === null) return "border-gray-200";
    if (inRange)
      return "border-emerald-500 bg-emerald-50 focus:border-emerald-500 focus:ring-emerald-200";
    return "border-rose-500 bg-rose-50 focus:border-rose-500 focus:ring-rose-200";
  };

  const updateValue = (i, value) => {
    try {
      setResults((prev) => {
        const copy = [...prev];
        copy[i] = { ...copy[i], value };
        return copy;
      });
    } catch (err) {
      console.error("updateValue error:", err);
    }
  };

  const renderResultField = (r, i) => {
    const t = String(r.type || "").toLowerCase().replace(/\s+/g, "");

    if (t === "options") {
      const opts = r.originalParameter?.resultOpts || [];
      return (
        <select
          disabled={isLocked}
          className="text-sm border border-gray-300 px-3 py-2.5 w-full rounded-lg bg-white disabled:bg-gray-100 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
          value={r.value || ""}
          onChange={(e) => updateValue(i, e.target.value)}
        >
          <option value="">Select</option>
          {opts.map((o) => (
            <option key={o.id ?? `${o.value}-${o.label}`} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    if (t === "numeric") {
      return (
        <div className="relative">
          <input
            disabled={isLocked}
            className={`text-sm border px-3 py-2.5 w-full rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed focus:outline-none focus:ring-2 transition-colors ${getResultColor(
              r.value,
              r.referenceRange
            )}`}
            value={r.value}
            onChange={(e) => updateValue(i, e.target.value)}
            placeholder="Enter value"
            inputMode="decimal"
          />
          {r.value && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div
                className={`w-2 h-2 rounded-full ${
                  isValueInRange(r.value, r.referenceRange) === true
                    ? "bg-emerald-500"
                    : isValueInRange(r.value, r.referenceRange) === false
                    ? "bg-rose-500"
                    : "bg-gray-300"
                }`}
              />
            </div>
          )}
        </div>
      );
    }

    // ✅ Free Text — full rich text editor (for radiology / impressions)
    if (t === "freetext" || t === "free_text") {
      return (
        <div className={`border border-gray-200 rounded-lg overflow-hidden ${ isLocked ? "opacity-70 pointer-events-none" : "" }`}>
          <TinyMCEFullEditor
            value={r.value || ""}
            onChange={(val) => updateValue(i, val)}
            height={220}
            placeholder="Type findings / impression here..."
          />
        </div>
      );
    }

    if (t === "textarea") {
      return (
        <textarea
          disabled={isLocked}
          className="text-sm border border-gray-300 px-3 py-2.5 w-full rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors min-h-[80px] resize-y"
          value={r.value}
          onChange={(e) => updateValue(i, e.target.value)}
          rows={3}
        />
      );
    }

    return (
      <input
        disabled={isLocked}
        className="text-sm border border-gray-300 px-3 py-2.5 w-full rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
        value={r.value}
        onChange={(e) => updateValue(i, e.target.value)}
        placeholder="Enter value"
      />
    );
  };

  /* ---------------- Signature Helpers ---------------- */
  const sigById = useMemo(() => {
    const map = new Map();
    sigOptions.forEach((s) => map.set(s.id, s));
    return map;
  }, [sigOptions]);

  const SignatureSelect = ({ label, value, onChange }) => (
    <div className="w-full">
      <div className="text-xs font-semibold text-gray-700 mb-1">{label}</div>
      <select
        disabled={isLocked || sigLoading}
        className="text-sm border border-gray-300 px-3 py-2.5 w-full rounded-lg bg-white disabled:bg-gray-100 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value ? e.target.value : null)}
      >
        <option value="">None</option>
        {sigOptions.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} {s.designation ? `- ${s.designation}` : ""} (
            {String(s.alignment || "").toUpperCase()})
          </option>
        ))}
      </select>

      {value && sigById.get(value) && (
        <div className="mt-2 flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
          <img
            src={sigById.get(value).signatureImg}
            alt="signature"
            className="h-10 w-auto object-contain bg-white rounded-md border border-gray-200 px-2"
          />
          <div className="text-xs text-gray-700">
            <div className="font-semibold">{sigById.get(value).name}</div>
            <div className="text-gray-500">
              {sigById.get(value).qualification || ""}{" "}
              {sigById.get(value).designation
                ? `• ${sigById.get(value).designation}`
                : ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const resetToDefaults = () => {
    try {
      setManualSigs({
        leftSignatureId: sigDefaults.left ?? null,
        centerSignatureId: sigDefaults.center ?? null,
        rightSignatureId: sigDefaults.right ?? null,
      });
    } catch (err) {
      console.error("resetToDefaults error:", err);
    }
  };

  /* ---------------- Reference Range Cell ---------------- */
  const renderReferenceRange = (r) => {
    // ✅ Priority 1: numeric limits (e.g. "9 - 10")
    if (String(r.referenceRange || "").trim()) {
      return (
        <div className="text-sm text-gray-700 font-medium">
          {r.referenceRange}
        </div>
      );
    }

    // ✅ Priority 2: normal value HTML
    if (r.normalValueHtml) {
      return (
        <div
          className="text-xs text-gray-700 leading-relaxed prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: r.normalValueHtml }}
        />
      );
    }

    // ✅ Priority 3: special condition HTML
    if (r.specialConditionHtml) {
      return (
        <div
          className="text-xs text-gray-700 leading-relaxed prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: r.specialConditionHtml }}
        />
      );
    }

    // ✅ Nothing available
    return <div className="text-xs text-gray-400">N/A</div>;
  };

  return (
    <div className="fixed inset-0 bg-gray-900/70 flex justify-center items-center p-3 z-50">
      <div className="bg-white w-full max-w-6xl rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white flex-shrink-0">
          <div className="flex justify-between items-center p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold">
                  Pathology Report - {test?.name || "Test"}
                </h2>
                <p className="text-sm text-blue-100 mt-0.5">
                  Enter laboratory results and values
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Patient Info */}
          <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4" />
                <span className="font-semibold">Patient</span>
              </div>
              <p className="text-base font-semibold">
                {patient?.fullName || "N/A"}
                {(patient?.gender || patient?.age || patient?.dob) && (
                  <span className="ml-2 text-sm font-normal text-blue-200">
                    ({[patient?.gender, formatPatientAge(patient)].filter(Boolean).join(", ")})
                  </span>
                )}
              </p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4" />
                <span className="font-semibold">Test</span>
              </div>
              <p className="text-base font-semibold">{test?.name || "N/A"}</p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4" />
                <span className="font-semibold">Status</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    isLocked
                      ? "bg-emerald-400"
                      : existingId
                      ? "bg-amber-400"
                      : "bg-blue-400"
                  }`}
                />
                <p className="text-base font-semibold">
                  {isLocked ? "Approved" : existingId ? "Draft" : "New"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Alert */}
        {isLocked && (
          <div className="bg-emerald-50 border border-emerald-200 mx-5 mt-5 p-4 rounded-lg flex-shrink-0">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-sm font-semibold text-emerald-900">
                  Approved Report
                </p>
                <p className="text-sm text-emerald-700 mt-0.5">
                  This report has been finalized and approved. No further edits
                  are permitted.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-3 border-gray-300 border-t-blue-600"></div>
              <p className="mt-4 text-sm text-gray-600">
                Loading parameters...
              </p>
            </div>
          ) : (
            <>
              {/* Results Table */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  Laboratory Results
                </label>

                <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="p-3 text-left font-semibold text-gray-700">
                          Parameter
                        </th>
                        <th className="p-3 text-left font-semibold text-gray-700">
                          Result
                        </th>
                        <th className="p-3 text-left font-semibold text-gray-700">
                          Reference Range
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {results
                        .filter((r) => {
                          const t = String(r.type || "").toLowerCase().replace(/\s+/g, "");
                          return t !== "freetext" && t !== "free_text";
                        })
                        .map((r, i) => {
                          const origIdx = results.indexOf(r);
                          return (
                            <tr
                              key={r.parameterId}
                              className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                            >
                              <td className="p-3">
                                <div className="font-medium text-gray-900">{r.name}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {r.type}{r.unit ? ` • ${r.unit}` : ""}
                                </div>
                              </td>
                              <td className="p-3">{renderResultField(r, origIdx)}</td>
                              <td className="p-3">{renderReferenceRange(r)}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {/* Free Text parameters — rendered as full-width cards */}
                {results
                  .filter((r) => {
                    const t = String(r.type || "").toLowerCase().replace(/\s+/g, "");
                    return t === "freetext" || t === "free_text";
                  })
                  .map((r) => {
                    const origIdx = results.indexOf(r);
                    return (
                      <div key={r.parameterId} className="mt-4 border border-indigo-100 rounded-xl overflow-hidden shadow-sm">
                        <div className="px-4 py-2.5 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
                          <div>
                            <span className="text-sm font-semibold text-indigo-900">{r.name}</span>
                            <span className="ml-2 text-xs text-indigo-500 bg-indigo-100 px-2 py-0.5 rounded-full">Free Text</span>
                          </div>
                        </div>
                        <div className="bg-white">
                          {renderResultField(r, origIdx)}
                        </div>
                      </div>
                    );
                  })}

              </div>

              {/* FREE_TEXT + RICH_TEXT Report Items — editors filled by doctor at result time */}
              {freeTextItems.length > 0 && (
                <div className="mb-6 space-y-4">
                  {freeTextItems.map((ft, idx) => {
                    const isRich = ft.type === "RICH_TEXT";
                    if (isRich) {
                      return (
                        <div
                          key={ft.id}
                          className={`border border-indigo-100 rounded-xl overflow-hidden shadow-sm ${
                            isLocked ? "opacity-70 pointer-events-none" : ""
                          }`}
                        >
                          <div className="flex items-stretch">
                            <div className="w-1/3 min-w-[160px] px-4 py-3 bg-indigo-50 border-r border-indigo-100 flex flex-col justify-center">
                              <span className="text-sm font-semibold text-indigo-900">{ft.title}</span>
                            </div>
                            <div className="flex-1 bg-white">
                              <textarea
                                value={htmlToPlainText(ft.html)}
                                onChange={(e) => {
                                  const text = e.target.value;
                                  setFreeTextItems((prev) => {
                                    const copy = [...prev];
                                    copy[idx] = { ...copy[idx], html: plainTextToHtml(text) };
                                    return copy;
                                  });
                                }}
                                rows={1}
                                className="w-full min-h-[40px] px-4 py-2 text-sm text-gray-800 outline-none resize-y focus:ring-1 focus:ring-indigo-500"
                                disabled={isLocked}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={ft.id} className="border border-rose-100 rounded-xl overflow-hidden shadow-sm">
                        <div className="px-4 py-2.5 border-b flex items-center gap-2 bg-rose-50 border-rose-100">
                          <span className="text-sm font-semibold text-rose-800">{ft.title}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full border text-rose-500 bg-rose-100 border-rose-200">
                            ✏️ Free Text
                          </span>
                        </div>
                        <div className={isLocked ? "opacity-70 pointer-events-none" : ""}>
                          <TinyMCEFullEditor
                            value={ft.html || ""}
                            onChange={(val) =>
                              setFreeTextItems((prev) => {
                                const copy = [...prev];
                                copy[idx] = { ...copy[idx], html: val };
                                return copy;
                              })
                            }
                            height={220}
                            placeholder={`Enter ${ft.title}...`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Notes */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Clinical Notes
                </label>

                <div
                  className={`rounded-lg border border-gray-300 overflow-hidden bg-white ${
                    isLocked ? "opacity-80" : ""
                  }`}
                >
                 

                   <TinyMCEFullEditor
                                              value={notes || ""}
                                              onChange={setNotes}
                                              height={200}
                                              placeholder="Write notes..."
                                            />
                </div>

                <div className="mt-1 text-xs text-gray-500">
                  You can format text (bold, list, headings).
                </div>
              </div>

              {/* Signature Overrides */}
              <div className="mb-6">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <PenLine className="w-4 h-4 text-gray-700" />
                    <div className="text-sm font-semibold text-gray-800">
                      Report Signatures (Manual Override)
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={resetToDefaults}
                    disabled={isLocked || sigLoading}
                    className="text-xs px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Reset to Category Default
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <SignatureSelect
                    label="Left Signature"
                    value={manualSigs.leftSignatureId}
                    onChange={(v) =>
                      setManualSigs((s) => ({ ...s, leftSignatureId: v }))
                    }
                  />
                  <SignatureSelect
                    label="Center Signature"
                    value={manualSigs.centerSignatureId}
                    onChange={(v) =>
                      setManualSigs((s) => ({ ...s, centerSignatureId: v }))
                    }
                  />
                  <SignatureSelect
                    label="Right Signature"
                    value={manualSigs.rightSignatureId}
                    onChange={(v) =>
                      setManualSigs((s) => ({ ...s, rightSignatureId: v }))
                    }
                  />
                </div>

                <div className="mt-2 text-xs text-gray-500">
                  These will be saved inside <b>PatientTestResult</b> for this
                  patient + test only.
                </div>
              </div>

              {/* Approval */}
              {!isLocked && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        id="approve-report"
                        checked={approved}
                        onChange={(e) => {
                          try {
                            setApproved(e.target.checked);
                          } catch (err) {
                            console.error("approve toggle error:", err);
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-400 rounded focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="approve-report"
                          className="text-sm font-semibold text-gray-900 cursor-pointer"
                        >
                          Finalize and Approve Report
                        </label>
                        <span className="px-2 py-1 text-xs font-bold bg-blue-100 text-blue-800 rounded-md">
                          ADMIN
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        Once approved, the report is final and cannot be edited.
                      </p>
                      {approved && (
                        <div className="mt-4 p-3 bg-blue-100 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-blue-700" />
                            <p className="text-sm font-medium text-blue-800">
                              The report will be locked upon saving.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="border-t border-gray-200 bg-white p-5 flex-shrink-0">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-gray-800">Report by:</span>
                <span className="text-gray-900">
                  {user?.name || user?.username || "N/A"}
                </span>
              </div>
              {existingResult?.updatedAt && (
                <div className="mt-1 text-xs text-gray-500">
                  <span className="font-medium">Last updated:</span>{" "}
                  {formatDate(existingResult.updatedAt)}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="text-sm px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>

              {!isLocked && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="text-sm px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 transition-all font-medium shadow-sm hover:shadow"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {existingId ? "Update Report" : "Save Report"}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PathologyResultModal;